import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Package, Calendar, Warehouse, Hash, Truck, CheckCircle2, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  findBranch, stockTransfersOf,
  TRANSFER_STAGE_META, locationLabelOf,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

const STAGE_STYLE = (stage) => {
  const meta = TRANSFER_STAGE_META[stage];
  return {
    background: meta ? `${meta.color}1a` : 'rgba(100,116,139,0.12)',
    color: meta?.color || '#475569',
  };
};

export const BranchReceivedDetailsTab = ({ branchId, org }) => {
  const { version: dataVersion } = useSharedStore();
  const branch = useMemo(() => findBranch(branchId), [branchId, dataVersion]);

  const rows = useMemo(() => {
    const incoming = stockTransfersOf(org.id).filter((t) => t.toLocationId === branchId && !(t.note || '').toLowerCase().includes('emergency'));
    const out = [];
    incoming.forEach((t) => {
      (t.lines || []).forEach((line, i) => {
        out.push({
          key: `${t.id}-${i}`,
          transferId: t.id,
          date: t.expectedDate || t.createdAt,
          from: t.fromLocationId,
          fromName: locationLabelOf(t.fromLocationId),
          name: line.name,
          label: line.label || '',
          qty: Number(line.qty) || 0,
          carrier: t.carrier || '',
          status: t.status,
        });
      });
    });
    // newest first
    return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [branchId, org.id, dataVersion]);

  if (!branch) {
    return (
      <div className="py-16 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
        Branch not found.
      </div>
    );
  }

  const accent = org.theme.primary;
  const totals = rows.reduce(
    (acc, r) => {
      acc.lines += 1;
      acc.qty += r.qty;
      if (r.status === 'received') acc.received += r.qty;
      else acc.inFlight += r.qty;
      return acc;
    },
    { lines: 0, qty: 0, received: 0, inFlight: 0 },
  );

  return (
    <div className="flex flex-col gap-5">
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Line items" value={totals.lines} icon={Package} color={accent} />
        <StatCard label="Total qty" value={totals.qty} icon={Hash} color={accent} />
        <StatCard label="Received" value={totals.received} icon={CheckCircle2} color="#0a7d3c" />
        <StatCard label="Pending" value={totals.inFlight} icon={Clock} color="#b45309" />
      </section>

      <motion.section
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[var(--theme-tertiary)] rounded-xl p-6 shadow-sm flex flex-col gap-3"
      >
        <header>
          <h3 className="text-base font-extrabold tracking-tight text-[#0d0d0d] inline-flex items-center gap-2">
            <Package size={16} style={{ color: accent }} aria-hidden="true" />
            Received details · {branch.name}
          </h3>
          <p className="text-[11px] font-medium text-slate-500 mt-1">
            Every line item shipped to this branch from any source warehouse, newest first.
          </p>
        </header>

        {rows.length === 0 ? (
          <div className="py-12 border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">Nothing received yet.</p>
            <p className="text-[11px] font-medium text-slate-500 mt-1">Once a warehouse dispatches stock to your branch, it shows up here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 border-b border-[var(--theme-tertiary)]">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">From</th>
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 pr-3">Variant</th>
                  <th className="py-2 pr-3">Qty</th>
                  <th className="py-2 pr-3">Carrier</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = TRANSFER_STAGE_META[r.status];
                  return (
                    <tr key={r.key} className="border-b border-[var(--theme-tertiary)] last:border-b-0">
                      <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                        {r.date ? new Date(r.date).toLocaleDateString('en-IN', { dateStyle: 'short' }) : '—'}
                      </td>
                      <td className="py-2 pr-3 text-slate-600 font-bold">
                        {r.fromName}
                      </td>
                      <td className="py-2 pr-3 font-bold text-[#0d0d0d]">{r.name}</td>
                      <td className="py-2 pr-3 text-slate-600">{r.label || 'Standard'}</td>
                      <td className="py-2 pr-3 font-bold" style={{ color: accent }}>{r.qty}</td>
                      <td className="py-2 pr-3 text-slate-600">
                        {r.carrier || '—'}
                      </td>
                      <td className="py-2 pr-3">
                        <span className="text-[11px] font-extrabold uppercase tracking-[0.12em]" style={{ color: STAGE_STYLE(r.status).color }}>
                          {meta?.label || r.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.section>
    </div>
  );
};

BranchReceivedDetailsTab.propTypes = {
  branchId: PropTypes.string.isRequired,
  org: PropTypes.object.isRequired,
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-4 py-3 flex items-center gap-3">
    <span className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: color }} aria-hidden="true">
      <Icon size={16} />
    </span>
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="text-base font-extrabold text-[#0d0d0d] leading-tight">{value}</p>
    </div>
  </div>
);

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
};
